import type { Message } from '../types';
import { Icon } from './Icon';
import { Markdown } from './Markdown';
import { Pop } from './Pop';
import { RequestDoc } from './RequestDoc';
import { digestRequest } from '../lib/requestDigest';
import { RelativeTime } from './RelativeTime';

export function MessageBubble({
  m,
  laneColor,
  onFocusNode,
  flash,
  avatar,
  author,
}: {
  m: Message;
  laneColor: string;
  onFocusNode?: (id: string) => void;
  flash?: boolean;
  /** l'avatar del pop che scrive: accanto alla bolla, come nella chat (tela, artboard 46) */
  avatar?: string | null;
  /** come si presenta chi scrive, «Pia · PM»; senza, l'autore così com'è */
  author?: string;
}) {
  const user = m.role === 'user';
  // la richiesta lunga che apre la sessione: sunto nella bolla, testo intero come allegato
  const digest = user && m.kind === 'request' ? digestRequest(m.text) : null;
  const sys = m.role === 'system';
  const agent = m.role === 'agent';
  const needsAnswer =
    (m.kind === 'rendezvous' ||
      m.kind === 'question' ||
      m.kind === 'clarification' ||
      m.kind === 'quota') &&
    !m.answered;

  if (sys) {
    return (
      <div className={flash ? 'anim-flash' : undefined} style={{ textAlign: 'center' }}>
        <span className="t-caption faint">{m.text}</span>
      </div>
    );
  }

  return (
    <div
      className={flash ? 'anim-flash' : undefined}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: user ? 'flex-end' : 'flex-start',
        paddingLeft: agent ? (avatar ? 66 : 24) : 0,
        position: 'relative',
        maxWidth: '100%',
      }}
    >
      {agent && avatar && (
        <span className="msg-face" data-tint="lilac">
          <Pop avatar={avatar} size={50} />
        </span>
      )}
      {agent && (
        <button
          onClick={() => m.fromNodeId && onFocusNode?.(m.fromNodeId)}
          className="msg-author"
          style={{
            border: 'none',
            background: 'none',
            padding: '0 0 4px',
            cursor: m.fromNodeId ? 'pointer' : 'default',
            textAlign: 'left',
          }}
        >
          {author ?? m.authorLabel}
        </button>
      )}
      {/* gli strumenti che il pop ha usato per questa risposta (es. send_email) */}
      {agent && (m.tools?.length ?? 0) > 0 && (
        <div className="msg-tools">
          {m.tools?.map((t) => (
            <span key={t} className="msg-tool">
              <Icon name="wrench" size={12} />
              {t}
            </span>
          ))}
        </div>
      )}
      {/* le bolle crescono con la chat, fino all'80% della sua larghezza */}
      <div
        style={{ display: 'flex', gap: 8, maxWidth: '80%', minWidth: 0, alignItems: 'flex-start' }}
      >
        {m.role === 'orchestrator' && (
          <span style={{ color: 'var(--accent)', display: 'grid', marginTop: 10, flex: 'none' }}>
            <Icon name="compass" size={20} />
          </span>
        )}
        <div
          style={{
            // le bolle della tela: le tue piene a caramella, quelle dei pop chiare con il bordo
            borderRadius: user ? '24px 24px 8px 24px' : '24px 24px 24px 8px',
            padding: '12px 16px',
            font: `${user ? 700 : 600} 15px/22px var(--font-ui)`,
            background: user ? 'var(--accent)' : 'var(--panel-2)',
            color: user ? 'var(--accent-ink)' : undefined,
            boxShadow: user ? '0 4px 0 var(--accent-edge)' : undefined,
            border: needsAnswer
              ? '2px dashed var(--ses-3)'
              : user
                ? 'none'
                : `2px solid ${agent ? 'var(--border)' : 'var(--border)'}`,
            borderLeft: agent && laneColor ? `4px solid ${laneColor}` : undefined,
            // il testo scritto da te tiene i suoi a capo (2026-11-02)
            whiteSpace: user ? 'pre-wrap' : undefined,
            overflowWrap: 'anywhere',
          }}
        >
          {m.transcribed && (
            <span
              className="t-caption faint"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 6 }}
            >
              <Icon name="mic" size={12} /> trascritto
            </span>
          )}
          {digest ? (
            <RequestDoc text={m.text} digest={digest} />
          ) : user ? (
            m.text
          ) : (
            <Markdown text={m.text} />
          )}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
        <RelativeTime at={m.at} />
        {m.deliveredVia.includes('voice') && (
          <span title="Annunciato a voce" style={{ color: 'var(--faint)', display: 'grid' }}>
            <Icon name="mic" size={12} />
          </span>
        )}
        {m.deliveredVia.includes('api') && (
          <span title="Recapitato via Telegram" style={{ color: 'var(--faint)', display: 'grid' }}>
            <Icon name="send" size={12} />
          </span>
        )}
      </div>
    </div>
  );
}
